import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { FC, useMemo } from 'react'

import Product, { ProductLite } from '@src/quince-site-common/src/domains/Product'
import Bundle, { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import PRODUCT_DOMAIN_TYPES from '@src/quince-site-common/src/domains/Product/ProductDomainTypes'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, { CUSTOM_FIELD_NAMES } from '@src/quince-site-common/src/helpers/productHelper'
import { formatPrice } from '@src/quince-site-common/src/utils/formatPrice'

import { ProductCardLink, ProductCardLinkProps } from '../product-card-actions/product-card-link'
import { useProductCardHover } from '../product-card-context/product-card-hover.context'
import { useProductCardContext } from '../product-card-context/product-card.context'

import styles from './product-title-section.module.scss'

// import {getFestivalOfferPrice} from '@src/quince-site-common/src/utils/festivalOffersUtility';

export type ProductTitleSectionProps = {
  titleClassName?: string
  productPrefixPriceClassName?: string
  priceClassName?: string
  hideProductName?: boolean
  productPriceClass?: string
  productPriceWrapperClass?: string
  nameAndPriceSectionClass?: string
} & ProductCardLinkProps

/**
 * keep seperate
 */
const getSelectedSectionTitle = (selectedOptions: ProductVariantOption[]) => {
  return selectedOptions
    .reduce((acc: string[], curr) => {
      acc.push(curr.value)
      return acc
    }, [])
    .join(' - ')
}

export const ProductTitleSection: FC<ProductTitleSectionProps> = props => {
  const { t } = useTranslation('common')
  const { setHovered } = useProductCardHover()
  const { product, selectedOptions, salePrice, festivalPrice } = useProductCardContext()
  const productName = product.title
  const brandName = useMemo(() => getBrandName(product), [product])
  const showFrom = product.type === PRODUCT_TYPES.BUNDLE || productHelper.hasDifferentPrices(product, selectedOptions)

  const selectedSectionTitle: string = useMemo(() => getSelectedSectionTitle(selectedOptions), [selectedOptions])

  return (
    <div
      className={classnames(styles.container, props.productPriceWrapperClass)}
      onMouseEnter={() => {
        setHovered(true)
      }}
      onMouseLeave={() => {
        setHovered(false)
      }}
    >
      <div
        className={classnames(styles.nameAndPriceSection, props.nameAndPriceSectionClass, {
          [styles.hideName]: props.hideProductName,
        })}
      >
        <div className={styles.productNameWrapper}>
          <h2 className={classnames(styles.productName, props.titleClassName)}>
            <ProductCardLink
              additionalAttributes={props.additionalAttributes}
              productClickTrackConfig={props.productClickTrackConfig}
              selectorKey={props.selectorKey}
              tracker={props.tracker}
              onClickCb={props.onClickCb}
              productCardLinkClass={props.productCardLinkClass}
            >
              {!props.hideProductName && productName}
              {selectedSectionTitle && <span className="sr-only">{selectedSectionTitle}</span>}
            </ProductCardLink>
          </h2>
        </div>
        <div
          className={classnames(styles.productPriceWrapper, props.productPriceClass, {
            [styles.festivalOfferWrapper]: festivalPrice,
          })}
        >
          <div className={classnames(styles.festivalOfferText)}>
            {showFrom && (
              <div className={classnames(styles.productPrefixPrice, props.productPrefixPriceClassName)}>
                {t('from')}
              </div>
            )}
            {festivalPrice && (
              <div className={classnames(props.priceClassName, styles.festivalPrice)}>{formatPrice(festivalPrice)}</div>
            )}
          </div>
          <div className={classnames(props.priceClassName, styles.basePrice)}>{formatPrice(salePrice)}</div>
        </div>
      </div>
      {brandName && <div className={styles.brandNameSection}>{brandName}</div>}
    </div>
  )
}

function getBrandName(product: Product | ProductLite | Bundle) {
  if (product.type === PRODUCT_TYPES.BUNDLE) return undefined
  const _brandName = isLiteProduct(product)
    ? product.productBrandInfo.displayName || product.productBrandInfo.name
    : productHelper.getCustomFieldValue<string>(product, CUSTOM_FIELD_NAMES.BRAND_NAME, true)
  return _brandName?.toLowerCase()
}

function isLiteProduct(product: Product | ProductLite): product is ProductLite {
  return product.productDomainTypes === PRODUCT_DOMAIN_TYPES.PRODUCT_LITE
}
